// Minimal wasm loader - replaces emscripten runtime

import type { Bytes } from "@pdfking/tfunc"
import type { Allocated } from "../types/Allocated"
import type {
  PdfiumArguments,
  PdfiumFunctions,
  PdfiumReturns,
} from "../types/Bindings"
import type { PdfiumApiFactory } from "../types/PdfiumApi"
import type { Pointer } from "../types/Pointer"
import type { WasmExports } from "../types/WasmExports"
import { UrlLoad } from "@pdfking/compatibility"
import {
  PdfiumExports as E,
  IMPORT_MODULE,
  pdfium as wasm,
} from "@pdfking/pdfium"
import {
  Constant,
  GuidToString,
  IsFunction,
  IsString,
  Keys,
  LockManager,
  StringToUInt8,
  UUID,
  Void,
} from "@pdfking/tfunc"

// Fixed date for deterministic PDF output (2024-01-01 00:00:00 UTC)
const DATE = Constant(1704067200000)
const EBADF = Constant(8)
const ENOENT = Constant(-44)
const EBADF_NEG = Constant(-8)
const ZERO = Constant(0)
const NOOP = Void

const fail = (error: Error | string) => {
  throw IsString(error) ? new Error(error) : error
}

const ERRORS: Record<number, string> = {
  1: "unknown error",
  2: "file not found or could not be opened",
  3: "file not in PDF format or corrupted",
  4: "password required or incorrect password",
  5: "unsupported security scheme",
  6: "page not found or content error",
}

const WASM_TYPE_NAMES: Record<string, string> = {
  i: "i32",
  j: "i64",
  f: "f32",
  d: "f64",
  p: "i32",
}

const WASM_TYPE_CODES: Record<string, number> = {
  i: 127,
  p: 127,
  j: 126,
  f: 125,
  d: 124,
}

// Emscripten-compatible function wrapping for callbacks
const sigToWasmTypes = (sig: string) => ({
  parameters: [...sig.slice(1)].map(c => WASM_TYPE_NAMES[c]),
  results: sig[0] === "v" ? [] : [WASM_TYPE_NAMES[sig[0]]],
})

const uleb128Encode = (n: number, target: number[]) => {
  if (n < 128) target.push(n)
  else target.push((n % 128) | 128, n >> 7)
}

const generateFuncType = (sig: string, target: number[]) => {
  const sigParam = sig.slice(1)
  target.push(96)
  uleb128Encode(sigParam.length, target)
  for (const c of sigParam) target.push(WASM_TYPE_CODES[c])
  if (sig[0] === "v") target.push(0)
  else target.push(1, WASM_TYPE_CODES[sig[0]])
}

const convertJsFunctionToWasm = (
  func: (...args: number[]) => number,
  sig: string
): WebAssembly.ExportValue => {
  // Modern browsers support WebAssembly.Function
  const WasmFunction = (WebAssembly as unknown as Record<string, unknown>)
    .Function as new (
    type: unknown,
    func: unknown
  ) => WebAssembly.ExportValue
  if (IsFunction(WasmFunction)) {
    return new WasmFunction(sigToWasmTypes(sig), func)
  }
  // Fallback: create tiny wasm module that imports and re-exports the function
  const typeSectionBody: number[] = [1]
  generateFuncType(sig, typeSectionBody)
  const bytes: number[] = [0, 97, 115, 109, 1, 0, 0, 0, 1]
  uleb128Encode(typeSectionBody.length, bytes)
  bytes.push(...typeSectionBody)
  bytes.push(2, 7, 1, 1, 101, 1, 102, 0, 0, 7, 5, 1, 1, 102, 0, 0)
  const module = new WebAssembly.Module(new Uint8Array(bytes))
  const instance = new WebAssembly.Instance(module, { e: { f: func } })
  return instance.exports.f
}

// Generic invoke for C++ exception handling / setjmp-longjmp
const createInvoke =
  (exports: WasmExports, returnsValue: boolean) =>
  (index: number, ...args: number[]) => {
    const sp = exports[E.emscripten_stack_get_current]()
    try {
      const fn = exports[E.__indirect_function_table].get(index) as
        | ((...a: number[]) => number)
        | null
      if (fn) return fn(...args)
      return 0
    } catch (e: unknown) {
      exports[E._emscripten_stack_restore](sp)
      if (typeof e !== "number") throw e
      exports[E.setThrew](1, 0)
      return returnsValue ? 0 : undefined
    }
  }

const createImportObject = () => {
  const exports = {} as WasmExports
  const invoke = createInvoke(exports, true)
  const invokeVoid = createInvoke(exports, false)
  const imports: WebAssembly.Imports = {
    [IMPORT_MODULE]: {
      [E.invoke_ii]: invoke,
      [E.invoke_iii]: invoke,
      [E.invoke_iiii]: invoke,
      [E.invoke_iiiii]: invoke,
      [E.invoke_viii]: invokeVoid,
      [E._tzset_js]: NOOP,
      [E._localtime_js]: NOOP,
      [E._gmtime_js]: NOOP,
      [E.emscripten_date_now]: DATE,
      [E.__syscall_fcntl64]: ZERO,
      [E.__syscall_ioctl]: ZERO,
      [E.__syscall_openat]: ENOENT,
      [E.__syscall_stat64]: ENOENT,
      [E.__syscall_newfstatat]: ENOENT,
      [E.__syscall_lstat64]: ENOENT,
      [E.__syscall_unlinkat]: ENOENT,
      [E.__syscall_rmdir]: ENOENT,
      [E.__syscall_fstat64]: EBADF_NEG,
      [E.__syscall_ftruncate64]: EBADF_NEG,
      [E.__syscall_getdents64]: EBADF_NEG,
      [E.emscripten_resize_heap]: (requestedSize: number) => {
        const oldSize = exports[E.memory].buffer.byteLength
        requestedSize >>>= 0
        const pagesToGrow = ((requestedSize - oldSize + 65535) / 65536) | 0
        if (pagesToGrow > 0) {
          try {
            exports[E.memory].grow(pagesToGrow)
            return 1
          } catch {
            return fail(
              `Failed to grow heap from ${oldSize} to ${requestedSize} bytes`
            )
          }
        }
        return 1
      },
      [E._abort_js]: () => fail("abort"),
      [E._emscripten_throw_longjmp]: () => fail("longjmp"),
      [E.__cxa_throw]: () => fail("C++ exception thrown"),
      [E._emscripten_runtime_keepalive_clear]: NOOP,
      [E._setitimer_js]: ZERO,
      [E.fd_close]: EBADF,
      [E.fd_write]: EBADF,
      [E.fd_read]: EBADF,
      [E.fd_seek]: EBADF,
      [E.fd_sync]: ZERO,
      [E.environ_get]: ZERO,
      [E.environ_sizes_get]: (countPtr: number, sizePtr: number) => {
        const heap = new DataView(exports[E.memory].buffer)
        heap.setInt32(countPtr, 0, true)
        heap.setInt32(sizePtr, 0, true)
        return 0
      },
      [E.proc_exit]: (code: number) =>
        fail(`proc_exit called with code ${code}`),
    },
  }
  return { imports, exports }
}

const loadPdfiumWasm = async (response: Response) => {
  // Compile module once, reuse for clear()
  const module = await WebAssembly.compileStreaming(response)
  const { schedule } = LockManager()

  return async () => {
    const id = GuidToString(UUID())

    const { imports, exports } = createImportObject()

    const init = () =>
      WebAssembly.instantiate(module, imports).then(instance => {
        // biome-ignore lint/style/noRestrictedGlobals: WebAssembly instance exports assignment
        Object.assign(exports, instance.exports)
        exports[E.__wasm_call_ctors]()
        exports[E.FPDF_InitLibrary]()
      })

    await init()

    const getHeap = () => new Uint8Array(exports[E.memory].buffer)

    const getLastError = async (): Promise<Error | undefined> => {
      const code = await call(E.FPDF_GetLastError)
      return ERRORS[code] ? new Error(ERRORS[code]) : undefined
    }

    const call = async <M extends PdfiumFunctions>(
      method: M,
      ...args: PdfiumArguments[M]
    ): Promise<PdfiumReturns[M]> => {
      const fn = exports[method] as ((...a: number[]) => number) | undefined
      if (!fn) return fail(`Function ${method} not found`)
      return schedule(id, async () => fn(...args)) as Promise<PdfiumReturns[M]>
    }

    const safeCall = async <M extends PdfiumFunctions>(
      method: M,
      ...args: PdfiumArguments[M]
    ): Promise<PdfiumReturns[M]> => {
      const result = await call(method, ...args)
      if (!result) fail((await getLastError()) || `Failed: ${method}`)
      return result
    }

    const malloc = (size: Bytes): Allocated => {
      const pointer = exports[E.malloc](size) as Pointer
      return { pointer, free: () => exports[E.free](pointer) }
    }

    const writeString = (str: string): Allocated => {
      const bytes = StringToUInt8(str)
      const { pointer, free } = malloc((bytes.length + 1) as Bytes)
      writeTo(pointer, bytes)
      getHeap()[pointer + bytes.length] = 0
      return { pointer, free }
    }

    const writeBytes = (data: Uint8Array): Allocated => {
      const { pointer, free } = malloc(data.length as Bytes)
      writeTo(pointer, data)
      return { pointer, free }
    }

    const writeTo = (ptr: Pointer, data: Uint8Array) => getHeap().set(data, ptr)

    const readBytes = (ptr: Pointer, size: Bytes) =>
      new Uint8Array(exports[E.memory].buffer, ptr, size).slice()

    const readFloats = (ptr: Pointer, count: number) =>
      new Float32Array(exports[E.memory].buffer, ptr, count)

    const setInt32 = (ptr: Pointer, value: number) =>
      new DataView(exports[E.memory].buffer).setInt32(ptr, value, true)

    const addCallback = (
      fn: (...args: number[]) => number,
      sig: string
    ): Allocated => {
      const wrapped = convertJsFunctionToWasm(fn, sig)
      const table = exports[E.__indirect_function_table]
      const index = table.length
      table.grow(1)
      table.set(index, wrapped)
      const pointer = index as Pointer
      return {
        pointer,
        free: () => exports[E.__indirect_function_table].set(index, null),
      }
    }

    const getMemorySize = () => exports[E.memory].buffer.byteLength as Bytes

    const dispose = async () => {
      for (const key of Keys(exports)) {
        delete exports[key]
      }
    }

    return {
      id,
      call,
      safeCall,
      malloc,
      writeString,
      writeBytes,
      writeTo,
      readBytes,
      readFloats,
      setInt32,
      addCallback,
      getMemorySize,
      init,
      dispose,
    }
  }
}

export const CreatePdfiumFactory: PdfiumApiFactory = (path?: string) =>
  UrlLoad(path || wasm, "application/wasm").then(loadPdfiumWasm)
